
import React, { useState, useEffect } from "react";
import { ArrowUpIcon, PlusCircle, Send, Calendar, Clock, AlertTriangle, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";

// Pagamentos vazios para iniciar
const pagamentosVazios = [];

const PagamentosSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState("proximos");
  const [pagamentos, setPagamentos] = useState<any[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    // Simulando carregamento de dados - em produção isso viria de uma API
    setPagamentos(pagamentosVazios);
  }, [activeTab]);

  const handleNovoPagamento = () => {
    // Navegar para o formulário de novo pagamento
    const event = new CustomEvent('change-tab', { 
      detail: { tab: 'pagamentos', action: 'novo-pagamento' } 
    });
    window.dispatchEvent(event);
    
    toast({
      title: "Novo pagamento",
      description: "Redirecionando para o formulário de agendamento de pagamentos",
    });
  };

  const handleSalvarPagamentos = () => {
    // Aqui seria a lógica para salvar no banco de dados
    toast({
      title: "Pagamentos salvos",
      description: "Os pagamentos foram salvos com sucesso",
      duration: 3000,
    });
  };

  // Calcular o total dos pagamentos
  const totalPagamentos = pagamentos.reduce((acc, item) => acc + (item.valor || 0), 0);

  return (
    <div className="h-full">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center">
          <ArrowUpIcon className="h-4 w-4 text-gray-500" />
          <h2 className="text-xs ml-2 text-gray-700">Pagamentos</h2>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-600">
            {totalPagamentos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSalvarPagamentos}
            className="text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-600 text-xs"
          >
            <Save className="h-3 w-3 mr-1" /> Salvar
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-gray-50 rounded-none border-b border-gray-100">
          <TabsTrigger 
            value="proximos" 
            className={`flex-1 py-2 px-4 text-center text-xs ${activeTab === "proximos" ? "border-b-2 border-gray-400 font-normal" : "text-gray-500"}`}
          >
            Próximos a pagar
          </TabsTrigger>
          <TabsTrigger 
            value="abertos" 
            className={`flex-1 py-2 px-4 text-center text-xs ${activeTab === "abertos" ? "border-b-2 border-gray-400 font-normal" : "text-gray-500"}`}
          >
            Em aberto
          </TabsTrigger>
          <TabsTrigger 
            value="vencidos" 
            className={`flex-1 py-2 px-4 text-center text-xs ${activeTab === "vencidos" ? "border-b-2 border-gray-400 font-normal" : "text-gray-500"}`}
          >
            Vencido
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab}>
          {pagamentos.length > 0 ? (
            <div className="">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="w-[100px] text-xs text-gray-500">Data</TableHead>
                    <TableHead className="text-xs text-gray-500">Descrição</TableHead>
                    <TableHead className="text-right text-xs text-gray-500">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagamentos.map(pagamento => (
                    <TableRow key={pagamento.id} className="hover:bg-gray-50">
                      <TableCell className="text-xs text-gray-600">
                        {format(new Date(pagamento.dataVencimento), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell>
                        <p className="text-xs text-gray-600">{pagamento.descricao}</p>
                        <p className="text-xs text-gray-500">{pagamento.fornecedor}</p>
                      </TableCell>
                      <TableCell className="text-right text-xs text-gray-600">
                        {pagamento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="p-3 text-center">
                <Button 
                  variant="link" 
                  className="text-gray-500 hover:text-gray-600 text-xs"
                  onClick={() => {
                    // Navegar para a aba completa de pagamentos
                    const event = new CustomEvent('change-tab', { detail: { tab: 'pagamentos' } });
                    window.dispatchEvent(event);
                  }}
                >
                  Ver todos os pagamentos
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-500">
              <div className="text-center">
                <Send className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Nenhum pagamento {activeTab === "proximos" ? "pendente" : activeTab === "abertos" ? "em aberto" : "vencido"}</p>
                <Button 
                  className="mt-4 text-xs py-1 h-auto bg-gray-200 text-gray-600 hover:bg-gray-300"
                  onClick={handleNovoPagamento}
                >
                  <PlusCircle className="h-3 w-3 mr-1" /> Novo pagamento
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PagamentosSection;
